import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ArrowIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { FadeIn } from '../../components/motion/index';
import { Company, SnapshotHooks, useLocale, useNavigate } from '../../hooks/index';
import { capTableStore } from '../../storage/local/index';
import { stakeholderTypeTranslation } from '../../translations/stakeholder-translation.util';
import { PageRoute } from '../../types/page.types';
import { StakeholderType } from '../../types/stakeholder.types';
import { CapTableMobilePagePlaceholder } from './components/cap-table-loading-placeholders';
import { DetailsItem } from './details-item';
import { sortByIssuedPercentageDesc } from './utils/sort-cap-table-rows.util';
import { latestSnapshotId } from './variables';

export const CapTableMobileStakeholderDetailsPage: FC = () => {
  const { stakeholderType, id } = useParams<{ id: string; stakeholderType: StakeholderType }>();

  const { messagesLocale } = useLocale();
  const { selectedCompanyId } = Company.useSelected();
  const { isLoading, snapshots } = SnapshotHooks.useSnapshots({ companyId: selectedCompanyId });
  const { snapshot: currentSnapshot, isLoading: isLoadingCurrent } = SnapshotHooks.useCurrent({
    companyId: selectedCompanyId,
  });
  const { snapshot: snapshotByDate, isLoading: isLoadingSnapshotByDate } =
    SnapshotHooks.useSnapshotByDate({
      companyId: selectedCompanyId,
      date: capTableStore.get()?.customCapTableDate,
    });

  // List rows are slim (no full stats) — resolve the route's valuation/incorporation id to a
  // snapshot id and fetch the full row by id (falling back to the first row, as before).
  const resolvedListRowId =
    latestSnapshotId === id || snapshotByDate
      ? undefined
      : (
          snapshots.find(({ valuation }) => valuation?.id === id) ??
          snapshots.find(({ companyIncorporation }) => companyIncorporation?.id === id) ??
          snapshots[0]
        )?.id;

  const { snapshot: snapshotById, isLoading: isLoadingSnapshotById } = SnapshotHooks.useSnapshot({
    companyId: selectedCompanyId,
    snapshotId: resolvedListRowId ?? '',
  });

  const snapshot = latestSnapshotId === id ? currentSnapshot : snapshotByDate || snapshotById;

  const navigate = useNavigate(PageRoute.CAP_TABLE);

  const stakeholdersForType = useMemo(
    () =>
      sortByIssuedPercentageDesc(
        snapshot?.stats?.stakeholders.filter(
          ({ type, shareClasses }) =>
            type === stakeholderType &&
            shareClasses.reduce((sharesTotal, { issued }) => sharesTotal + issued, 0) > 0,
        ) ?? [],
      ),
    [snapshot?.stats?.stakeholders, stakeholderType],
  );

  const isPageLoading =
    isLoading ||
    isLoadingSnapshotByDate ||
    isLoadingSnapshotById ||
    (latestSnapshotId === id && isLoadingCurrent);

  if (isPageLoading) return <CapTableMobilePagePlaceholder />;

  return (
    <FadeIn>
      <div className="flex w-full flex-col gap-4 rounded-md bg-white px-3 py-4 shadow-sm">
        <div className="flex cursor-pointer gap-2 pt-4 text-xs font-[450] text-gray-400">
          <span className="text-brand-700" onClick={() => navigate()}>
            <AppFormattedMessage id="CAP_TABLE" />
          </span>
          <span>/</span>
          <span className="text-brand-700" onClick={() => navigate([id || ''])}>
            <AppFormattedMessage id="POSITIONS" />
          </span>
          <span>/</span>
          <span>
            {stakeholderType && messagesLocale[stakeholderTypeTranslation[stakeholderType]]}
          </span>
        </div>
        <div className="flex items-center justify-between border-y border-gray-100 py-3">
          <div className="flex items-center gap-2">
            <Button
              className="centered size-9 rounded-full"
              onClick={() => navigate([id || ''])}
              style={{
                boxShadow: '0px 0px 24px 0px #00000014',
                border: '1px solid #F2F2F2',
              }}
              styleType="NONE"
            >
              <ArrowIcon />
            </Button>
            <div className="flex flex-col">
              <span className="text-base font-semibold text-gray-700">
                {stakeholderType && messagesLocale[stakeholderTypeTranslation[stakeholderType]]}
              </span>
              <span className="text-xs font-[450] text-gray-400">
                <AppFormattedMessage id="DETAILS" />
              </span>
            </div>
          </div>
        </div>
        {stakeholdersForType.map((stakeholder) => (
            <DetailsItem
              avatar
              key={stakeholder.id}
              {...stakeholder}
              description={messagesLocale[stakeholderTypeTranslation[stakeholder.type]]}
            />
          ))}
      </div>
    </FadeIn>
  );
};
